import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"

import { Pagination } from "components"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { useAuth } from "contexts/auth-context"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import useIntercompanySalesReportRepositories from "repositories/intercompany-sales-report"

// ATALLA-383: Laporan Penjualan Antar Toko — per produk × outlet.
// Backend menyaring PRODUK per entity user (admin PT Atalla/Vision hanya melihat
// produknya sendiri via EntityScope); outlet TIDAK dibatasi supaya penjualan produk
// di toko lain terlihat.

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")
const formatCurrency = (value) => Number(value ?? 0).toLocaleString("id-ID", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export default function List() {
  const { isSuper, can } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const startOfMonth = toInputDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const dateFrom = searchParams.get("date_from") || startOfMonth
  const dateTo = searchParams.get("date_to") || toInputDate(new Date())
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const search = searchParams.get("search") || ""
  // Kanal order: ALL | POS (Atalla POS).
  const orderSource = searchParams.get("order_source") || "ALL"
  // ATALLA-419: paginasi server-side (menggantikan cap 1000 lama).
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "50"

  // Laporan lintas toko — di-gate permission halaman.
  const canView = isSuper() || can("intercompany-sales-report.view")

  const { data: reportData, isLoading } = useIntercompanySalesReportRepositories({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_id: resolvedOutletCsv || undefined,
    search: search || undefined,
    order_source: orderSource === "ALL" ? undefined : orderSource,
    page,
    per_page: perPage,
    enabled: canView,
  })

  const rows = useMemo(() => {
    if (Array.isArray(reportData?.items?.data)) return reportData.items.data
    return Array.isArray(reportData?.rows) ? reportData.rows : []
  }, [reportData])

  const pagination = reportData?.items

  // Ubah link paginator (absolute API url) → query-string lokal, pertahankan filter.
  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []
    return links.map((link) => {
      if (!link?.url) return link
      const nextParams = new URLSearchParams(searchParams)
      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }
      return { ...link, url: `?${nextParams.toString()}` }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0
    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0
    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  // Ganti filter → reset ke halaman 1 (kecuali saat mengubah "page" itu sendiri).
  const patch = (key, value) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== "page") next.set("page", "1")
    setSearchParams(next)
  }

  if (!canView) {
    return (
      <div className="space-y-2">
        <h1 className="text-lg font-semibold text-neutral-900">Penjualan Antar Toko</h1>
        <p className="text-xs text-neutral-500">
          Anda tidak memiliki akses untuk melihat laporan ini. Hubungi admin untuk meminta akses.
        </p>
      </div>
    )
  }

  return (
    <div className="pb-4 space-y-6 lg:pb-8">
      <div>
        <h1 className="text-lg font-semibold text-neutral-900">Laporan Penjualan Antar Toko</h1>
        <p className="text-xs text-neutral-500">
          Penjualan per produk di tiap outlet. Untuk admin PT Atalla/Vision hanya produk milik entity-nya yang tampil.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 text-xs">
        <div className="space-y-1">
          <label className="block font-medium text-neutral-700">Dari Tanggal</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => patch("date_from", e.target.value)}
            className="px-3 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
          />
        </div>
        <div className="space-y-1">
          <label className="block font-medium text-neutral-700">Sampai Tanggal</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => patch("date_to", e.target.value)}
            className="px-3 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
          />
        </div>
        <div className="space-y-1">
          <label className="block font-medium text-neutral-700">Outlet</label>
          <OutletFilterSelect
            value={outletId}
            onChange={(v) => patch("outlet_id", v)}
            className="px-3 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            title="Outlet"
          />
        </div>
        <div className="space-y-1">
          <label className="block font-medium text-neutral-700">Sumber</label>
          <select
            value={orderSource}
            onChange={(e) => patch("order_source", e.target.value === "ALL" ? "" : e.target.value)}
            className="px-3 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
          >
            <option value="ALL">Semua Kanal</option>
            <option value="POS">Atalla (POS)</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="block font-medium text-neutral-700">Cari Produk</label>
          <input
            type="text"
            defaultValue={search}
            placeholder="Nama / SKU produk"
            onKeyDown={(e) => {
              if (e.key === "Enter") patch("search", e.currentTarget.value.trim())
            }}
            onBlur={(e) => patch("search", e.currentTarget.value.trim())}
            className="px-3 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
          />
        </div>
        {/* ATALLA-400: export excel */}
        <div className="space-y-1 flex items-end">
          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams({
                date_from: dateFrom,
                date_to: dateTo,
                ...(resolvedOutletCsv ? { outlet_id: resolvedOutletCsv } : {}),
                ...(search ? { search } : {}),
                ...(orderSource && orderSource !== "ALL" ? { order_source: orderSource } : {}),
              })
              window.open(`${process.env.REACT_APP_INVENTORY_API_URL}/reports/intercompany-sales/export?${params.toString()}`, "_blank")
            }}
            className="inline-flex items-center px-4 py-2 space-x-2 text-white transition bg-green-600 rounded-xl active:hover:scale-95"
          >
            <span>Export Excel</span>
          </button>
        </div>
        {/* ATALLA-419: jumlah baris per halaman */}
        <div className="space-y-1">
          <label className="block font-medium text-neutral-700">Baris / Halaman</label>
          <select
            value={perPage}
            onChange={(e) => patch("per_page", e.target.value)}
            className="px-3 py-2 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
          >
            {["20", "50", "100", "200"].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto border border-neutral-200 rounded-xl">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              {/* ATALLA-398: kolom tanggal + jam (granularity per order dari backend) */}
              <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Tanggal</th>
              <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Jam</th>
              <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">No Order</th>
              <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Produk</th>
              <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Outlet</th>
              <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Sumber</th>
              <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500 whitespace-nowrap">Qty (PCS)</th>
              <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500 whitespace-nowrap">Nilai Penjualan</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-200">
            {isLoading && (
              <tr><td colSpan={8} className="px-3 py-6 text-xs text-center text-neutral-500">Memuat data…</td></tr>
            )}

            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-xs text-center text-neutral-500">
                {reportData?.note || "Tidak ada penjualan pada rentang ini."}
              </td></tr>
            )}

            {!isLoading && rows.map((row, idx) => (
              <tr key={`${row.order_id || idx}-${row.product_unit_id}-${row.outlet_id}`} className="hover:bg-brand-50 transition-colors">
                <td className="px-3 py-3 text-xs text-neutral-800 whitespace-nowrap">{row.order_datetime ? moment(row.order_datetime).format("DD/MM/YYYY") : "-"}</td>
                <td className="px-3 py-3 text-xs text-neutral-800 whitespace-nowrap">{row.order_datetime ? moment(row.order_datetime).format("HH:mm") : "-"}</td>
                <td className="px-3 py-3 text-xs text-neutral-800 whitespace-nowrap">{row.order_id || "-"}</td>
                <td className="px-3 py-3 text-xs text-neutral-800">{row.product_unit_name || row.product_unit_id}</td>
                <td className="px-3 py-3 text-xs text-neutral-800">{row.outlet_name || row.outlet_id}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                    String(row.order_source).toUpperCase() === "YOIBOI"
                      ? "bg-blue-50 text-blue-700"
                      : "bg-neutral-100 text-neutral-700"
                  }`}>
                    {String(row.order_source).toUpperCase() === "YOIBOI" ? "Online" : "Atalla (POS)"}
                  </span>
                </td>
                <td className="px-3 py-3 text-xs text-right text-neutral-800">{formatNumber(row.qty)}</td>
                <td className="px-3 py-3 text-xs text-right text-neutral-800">{formatCurrency(row.value)}</td>
              </tr>
            ))}
          </tbody>

          {!isLoading && rows.length > 0 && (
            <tfoot className="bg-neutral-50">
              <tr>
                <td colSpan={6} className="px-3 py-3 text-xs font-semibold text-neutral-900">Total</td>
                <td className="px-3 py-3 text-xs font-semibold text-right text-neutral-900">{formatNumber(reportData?.total_qty)}</td>
                <td className="px-3 py-3 text-xs font-semibold text-right text-neutral-900">{formatCurrency(reportData?.total_value)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ATALLA-419: kontrol paginasi server-side */}
      {!isLoading && pagination && Number(pagination.total ?? 0) > 0 && (
        <div className="pt-2">
          <Pagination
            links={paginationLinks}
            from={paginationFrom}
            to={paginationTo}
            total={pagination?.total}
          />
        </div>
      )}
    </div>
  )
}
